import { useEffect, useState } from 'react'

import Protected from '@/components/Protected'
import EditorHost from '@/editor/EditorHost'
import Home from '@/pages/home'
import Register from '@/pages/register'
import UserAccount from '@/pages/userAccount'
import UserList from '@/pages/userList'
import {
  AccountInformation,
  AccountSettingsTab,
  EditorSettings,
  InspectionSheetSettings,
  Plan,
} from '@infraloop/account-settings'
import { ErrorPage, NotFound } from '@infraloop/common'
import { BlueprintEditor, Editor, InspectionSheet, OldInspectionAreaURLRedirect, PCDAlignment } from '@infraloop/editor'
import * as Sentry from '@sentry/react'
import { Navigate, createBrowserRouter, useLocation, useRouteError } from 'react-router'

const RouteError = () => {
  const error = useRouteError()
  const [eventId, setEventId] = useState<string>()

  useEffect(() => {
    setEventId(Sentry.captureException(error))
  }, [error])

  const code = eventId ? `ERR-${eventId.slice(0, 4).toUpperCase()}-${eventId.slice(4, 8).toUpperCase()}` : undefined

  return <ErrorPage errorCode={code} />
}

/**
 * Sends the legacy dashboard address to the root, query and hash intact — the report
 * exports write project links as `/dashboard#<project id>`, and the dashboard reads that
 * id to open the right row.
 */
const DashboardRedirect = () => {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/', search, hash }} replace />
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      // The root and the sign-up form are reachable signed out: without them a visitor is
      // handed straight to the Auth0 login screen, with no way to create an account.
      {
        id: 'root',
        index: true,
        Component: Home,
      },
      {
        id: 'registration',
        path: 'register',
        Component: Register,
      },
      {
        // The dashboard lived at /dashboard in the legacy app; older links and mails
        // (including the ones inviting people to register) still point there.
        path: 'dashboard',
        element: <DashboardRedirect />,
      },
      {
        element: <Protected />,
        children: [
          {
            path: 'user',
            Component: UserAccount,
            children: [
              { index: true, element: <Navigate to={AccountSettingsTab.Account} replace /> },
              { id: AccountSettingsTab.Account, path: AccountSettingsTab.Account, Component: AccountInformation },
              {
                id: AccountSettingsTab.InspectionSheet,
                path: AccountSettingsTab.InspectionSheet,
                Component: InspectionSheetSettings,
              },
              { id: AccountSettingsTab.Editor, path: AccountSettingsTab.Editor, Component: EditorSettings },
              { id: AccountSettingsTab.Billing, path: AccountSettingsTab.Billing, Component: Plan },
            ],
          },
          {
            // Admin-only; the page itself redirects non-admins away.
            id: 'user-list',
            path: 'users',
            Component: UserList,
          },
          {
            // The shared editor body (`@infraloop/editor`) hosted in-app under the Hatsuly
            // editor header. Route shape is frozen — customer-distributed links point here.
            id: 'project',
            path: 'projects',
            Component: EditorHost,
            children: [
              {
                id: 'project-inspection-area',
                path: ':project_id/inspection-area/:inspection_area_id',
                children: [
                  {
                    id: 'inspection-area-editor',
                    index: true,
                    Component: OldInspectionAreaURLRedirect,
                  },
                  {
                    id: 'inspection-area-blueprint',
                    path: 'blueprints',
                    Component: OldInspectionAreaURLRedirect,
                  },
                  {
                    id: 'inspection-area-inspection-sheet',
                    path: 'inspection-sheet',
                    Component: OldInspectionAreaURLRedirect,
                  },
                  {
                    id: 'inspection-area-pcd-alignment',
                    path: 'pcd-alignment',
                    Component: OldInspectionAreaURLRedirect,
                  },
                ],
              },
              {
                id: 'project-editor',
                path: ':project_id/editor',
                Component: Editor,
              },
              {
                id: 'project-blueprint-editor',
                path: ':project_id/blueprint-editor',
                Component: BlueprintEditor,
              },
              {
                id: 'project-inspection-sheet',
                path: ':project_id/inspection-sheet',
                Component: InspectionSheet,
              },
              {
                id: 'project-pcd-alignment',
                path: ':project_id/pcd-alignment',
                Component: PCDAlignment,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
])
